"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams, useNavigate } from "react-router-dom";

import FunnelProgressIndicator from "@/components/FunnelProgressIndicator";
import AgentIdDisplay from "@/components/AgentIdDisplay";
import FunnelStepRenderer from "@/components/FunnelStepRenderer";
import PlanSummary from "@/components/price-customization/PlanSummary";

import { useFunnelState, ProductVariant } from "@/hooks/useFunnelState";
import { usePremiumCalculation } from "@/hooks/usePremiumCalculation";
import { useOfferLoader } from "@/hooks/useOfferLoader";
import { useFunnelActions } from "@/hooks/useFunnelActions";
import { useFunnelNavigation } from "@/hooks/useFunnelNavigation";

const FunnelContainer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { locale } = useParams<{ locale: string }>();
  const currentLocale = locale || i18n.language;

  const {
    funnelState,
    setBundlePrices,
    setChipNumber,
    setSelectedProduct,
    setFinalPrice,
    setPreexistingCoverage,
    setWorldwideCoverage,
    setYearlyDeductible,
    setCoPayCoveragePercentage,
    setPaymentInterval,
    setPaymentMethod,
    setStartOfInsurance,
    setIsOfferLoadingFromUrl,
    handlePetSelection: handlePetSelectionState,
    handlePetDetailsSubmit: handlePetDetailsSubmitState,
    handlePriceCustomizationSubmit: handlePriceCustomizationSubmitState,
    handleContactDetailsSubmit: handleContactDetailsSubmitState,
    setFunnelStateFromOffer,
    handleResetFunnel,
    setDispatchContract,
    setDispatchInvoice,
    setDispatchReminder,
    setDispatchEmail,
    setAgentDispatchEmail,
    setWhitelabelPartner,
    setHasLeftConfirmationAfterOfferLoad,
    setConsultationProtocolDocument,
    clearConsultationProtocolDocument,
    setConsultationProtocolPending,
    clearConsultationProtocolPending,
  } = useFunnelState();

  const {
    petType,
    petName,
    breed,
    birthDate,
    selectedBreedType,
    gender,
    isCastrated,
    isHouseCat,
    selectedDogSize,
    chipNumber,
    bundlePrices,
    selectedProduct,
    finalPrice,
    contactFirstName,
    contactLastName,
    contactEmail,
    contactPhone,
    street,
    housenumber,
    city,
    zipCode,
    country,
    contactBirthdate,
    preexistingCoverage,
    worldwideCoverage,
    yearlyDeductible,
    coPayCoveragePercentage,
    paymentInterval,
    paymentMethod,
    startOfInsurance,
    firstDue,
    isOfferLoadingFromUrl,
    dispatchContract,
    dispatchInvoice,
    dispatchReminder,
    dispatchEmail,
    agentDispatchEmail,
    customerGender,
    whitelabelPartner,
    offerLoadedFromUrl,
    hasLeftConfirmationAfterOfferLoad,
    consultationProtocolPendingFile,
    consultationProtocolPendingFilename,
  } = funnelState;

  const getLocalizedPath = React.useCallback((pathKey: string) => {
    const baseRoute = i18n.getResource(currentLocale, 'translation', pathKey) as string;
    return `/${currentLocale}${baseRoute.startsWith('/') ? baseRoute : `/${baseRoute}`}`;
  }, [currentLocale, i18n]);

  const readWhitelabelFromLocation = React.useCallback(() => {
    const searchParams = new URLSearchParams(location.search || "");
    const fromSearch = searchParams.get("wl");
    if (fromSearch) return fromSearch;

    if (typeof window === "undefined") return null;
    const hash = window.location.hash;
    const hashQuery = hash.includes("?") ? hash.split("?")[1] : "";
    const hashParams = new URLSearchParams(hashQuery);
    const fromHash = hashParams.get("wl");
    return fromHash || null;
  }, [location.search]);

  React.useEffect(() => {
    const homePath = getLocalizedPath('routes.home');
    const petDetailsPath = getLocalizedPath('routes.petDetails');

    if (location.pathname === homePath && !location.state?.fromOfferLoad) {
      const wlpFromUrl = readWhitelabelFromLocation();
      handleResetFunnel();
      if (wlpFromUrl) {
        setWhitelabelPartner(wlpFromUrl);
      }
      navigate(petDetailsPath, { replace: true, state: { fromReset: true } });
    }
  }, [location.pathname, getLocalizedPath, handleResetFunnel, navigate, readWhitelabelFromLocation, setWhitelabelPartner]);

  const {
    goToHome,
    goToPetDetails,
    goToProductVariantSelection,
    goToProductCustomizationOptions,
    goToContactDetails,
    goToConfirmation,
  } = useFunnelNavigation({ funnelState, getLocalizedPath, handleResetFunnel });

  const {
    isLoading: isLoadingPrice,
    fetchInitialBundlePrices,
    currentApiPrice,
    underwritingFailed,
    underwritingReason,
    refetchPrice,
  } = usePremiumCalculation({
    petType,
    petName,
    breed,
    birthDate,
    selectedBreedType,
    gender,
    isCastrated,
    isHouseCat,
    selectedDogSize,
    selectedProduct,
    preexistingCoverage,
    worldwideCoverage,
    yearlyDeductible,
    coPayCoveragePercentage,
    paymentInterval,
    paymentMethod,
    startOfInsurance,
    contractId: funnelState.contractId,
    contractToken: funnelState.contractToken,
    agentId: funnelState.agentId,
    whitelabelPartner: funnelState.whitelabelPartner,
    currentLocale,
  });

  useOfferLoader({
    setFunnelStateFromOffer,
    handleResetFunnel,
    setIsOfferLoadingFromUrl,
    currentLocale,
  });

  React.useEffect(() => {
    if (!offerLoadedFromUrl) return;
    const confirmationPath = getLocalizedPath('routes.confirmation');
    if (location.pathname !== confirmationPath) {
      setHasLeftConfirmationAfterOfferLoad(true);
    }
  }, [offerLoadedFromUrl, location.pathname, getLocalizedPath, setHasLeftConfirmationAfterOfferLoad]);

  const {
    handlePetDetailsSubmitAndFetch,
    handleProductVariantSelectionAndNavigate: handleProductVariantSelectionAndNavigateAction,
    handleRequestOffer,
    handleCreateApplication,
    isSubmittingOffer,
  } = useFunnelActions({
    petType,
    petName,
    breed,
    birthDate,
    selectedBreedType,
    gender,
    isCastrated,
    isHouseCat,
    selectedDogSize,
    chipNumber,
    selectedProduct,
    preexistingCoverage,
    worldwideCoverage,
    yearlyDeductible,
    coPayCoveragePercentage,
    paymentInterval,
    paymentMethod,
    startOfInsurance,
    firstDue,
    contractId: funnelState.contractId,
    contractToken: funnelState.contractToken,
    agentId: funnelState.agentId,
    whitelabelPartner,
    dispatchContract,
    dispatchInvoice,
    dispatchReminder,
    dispatchEmail,
    agentDispatchEmail,
    contactBirthdate,
    customerGender: funnelState.customerGender,
    contactFirstName: funnelState.contactFirstName, // Pass contactFirstName
    contactLastName: funnelState.contactLastName,   // Pass contactLastName
    handlePetDetailsSubmitState,
    setBundlePrices,
    setSelectedProduct,
    setFinalPrice,
    handleContactDetailsSubmitState,
    fetchInitialBundlePrices,
    underwritingFailed,
    underwritingReason,
    currentLocale,
    handleResetFunnel,
    offerLoadedFromUrl,
    hasLeftConfirmationAfterOfferLoad,
    consultationProtocolPendingFile,
    consultationProtocolPendingFilename,
    setConsultationProtocolDocument,
    clearConsultationProtocolPending,
  });

  const handlePetSelectionAndNavigate = (selectedPetType: "cat" | "dog") => {
    handlePetSelectionState(selectedPetType);
    goToPetDetails();
  };

  const handlePetDetailsSubmitAndNavigate = async (
    submittedPetName: string,
    selectedBreed: string,
    submittedBreedLabel: string | null,
    submittedBirthDate: Date | undefined,
    submittedSelectedBreedType: "purebred" | "crossbreed" | "mixed",
    submittedGender: "male" | "female" | "unknown",
    submittedIsCastrated: boolean,
    submittedIsHouseCat: boolean,
    submittedSelectedDogSize: "small" | "large" | null,
    submittedPreexistingCoverage: boolean,
    submittedChipNumber: string | null
  ) => {
    await handlePetDetailsSubmitAndFetch(
      submittedPetName,
      selectedBreed,
      submittedBreedLabel,
      submittedBirthDate,
      submittedSelectedBreedType,
      submittedGender,
      submittedIsCastrated,
      submittedIsHouseCat,
      submittedSelectedDogSize,
      submittedPreexistingCoverage,
      submittedChipNumber
    );
  };

  const handleProductVariantSelectionAndNavigate = (product: ProductVariant) => {
    handleProductVariantSelectionAndNavigateAction(product);
  };

  const handlePriceCustomizationSubmitAndNavigate = (
    product: ProductVariant,
    price: number,
    preexisting: boolean,
    worldwide: boolean,
    deductible: number,
    coPayCoverage: number,
    interval: 1 | 2 | 4 | 12,
    method: "credit_card" | "ESR",
    startDate: Date
  ) => {
    handlePriceCustomizationSubmitState(product, price, preexisting, worldwide, deductible, coPayCoverage, interval, method, startDate);
    goToContactDetails();
  };

  const handleContactDetailsContinue = (
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    submittedStreet: string,
    submittedHousenumber: string,
    submittedCity: string,
    submittedZip: string,
    submittedCountry: string,
    submittedBirthdate: Date | undefined,
    submittedGender: "male" | "female" | ""
  ) => {
    handleContactDetailsSubmitState(
      firstName,
      lastName,
      email,
      phone,
      submittedStreet,
      submittedHousenumber,
      submittedCity,
      submittedZip,
      submittedCountry,
      submittedBirthdate,
      submittedGender
    );
    goToConfirmation();
  };

  React.useEffect(() => {
    // No redirect to a dedicated page anymore; errors are surfaced inline/toasts.
    if (underwritingFailed && underwritingReason) {
      console.warn("Underwriting failed:", underwritingReason);
    }
  }, [underwritingFailed, underwritingReason]);

  const showPlanSummary =
    location.pathname === getLocalizedPath('routes.productCustomization') ||
    location.pathname === getLocalizedPath('routes.addressDetails');

  return (
    <React.Fragment>
      <AgentIdDisplay />
      <FunnelProgressIndicator getLocalizedPath={getLocalizedPath} />

      {showPlanSummary && selectedProduct && (
        <div className="w-full max-w-4xl mx-auto sticky top-4 z-20 mb-8">
          <PlanSummary
            selectedProduct={selectedProduct}
            currentFinalPrice={currentApiPrice}
            paymentInterval={paymentInterval}
            isLoading={isLoadingPrice}
            yearlyDeductible={yearlyDeductible}
            coPayCoveragePercentage={coPayCoveragePercentage}
            preexistingCoverage={preexistingCoverage}
            worldwideCoverage={worldwideCoverage}
            underwritingFailed={underwritingFailed}
            underwritingReason={underwritingReason}
          />
        </div>
      )}

      <FunnelStepRenderer
        funnelState={funnelState}
        getLocalizedPath={getLocalizedPath}
        isLoadingPrice={isLoadingPrice}
        fetchInitialBundlePrices={fetchInitialBundlePrices}
        currentApiPrice={currentApiPrice}
        refetchPrice={refetchPrice}
        underwritingFailed={underwritingFailed}
        underwritingReason={underwritingReason}
        handlePetDetailsSubmitAndNavigate={handlePetDetailsSubmitAndNavigate}
        handleProductVariantSelectionAndNavigate={handleProductVariantSelectionAndNavigate}
        handlePriceCustomizationSubmitAndNavigate={handlePriceCustomizationSubmitAndNavigate}
        handleRequestOffer={handleRequestOffer}
        handleCreateApplication={handleCreateApplication}
        handleContactDetailsContinue={handleContactDetailsContinue}
        isSubmittingOffer={isSubmittingOffer}
        goToHome={goToHome}
        goToPetDetails={goToPetDetails}
        goToProductVariantSelection={goToProductVariantSelection}
        goToProductCustomizationOptions={goToProductCustomizationOptions}
        goToContactDetails={goToContactDetails}
        goToConfirmation={goToConfirmation}
        setSelectedProduct={setSelectedProduct}
        setChipNumber={setChipNumber}
        setPreexistingCoverage={setPreexistingCoverage}
        setWorldwideCoverage={setWorldwideCoverage}
        setYearlyDeductible={setYearlyDeductible}
        setCoPayCoveragePercentage={setCoPayCoveragePercentage}
        setPaymentInterval={setPaymentInterval}
        setPaymentMethod={setPaymentMethod}
        setStartOfInsurance={setStartOfInsurance}
        onPetTypeChange={handlePetSelectionState}
        setDispatchContract={setDispatchContract}
        setDispatchInvoice={setDispatchInvoice}
        setDispatchReminder={setDispatchReminder}
        setDispatchEmail={setDispatchEmail}
        setAgentDispatchEmail={setAgentDispatchEmail}
        setConsultationProtocolDocument={setConsultationProtocolDocument}
        clearConsultationProtocolDocument={clearConsultationProtocolDocument}
        setConsultationProtocolPending={setConsultationProtocolPending}
        clearConsultationProtocolPending={clearConsultationProtocolPending}
        setBundlePrices={setBundlePrices}
      />
    </React.Fragment>
  );
};

export default FunnelContainer;
